import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Video, Users, Plus, Edit, Trash2, User, Check, X, Image, Calendar, Clock, Power, Music, DollarSign, Eye, EyeOff, Settings } from 'lucide-react';
import { useApp } from '../context/AppContext';
import DragDropUpload from '../components/DragDropUpload';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPage = () => {
  const { user, refreshSectionVisibility } = useApp();
  const [videos, setVideos] = useState([]);
  const [students, setStudents] = useState([]);
  const [proofs, setProofs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [beats, setBeats] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('beats');
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState('video'); // 'video' or 'beat'
  const [editingItem, setEditingItem] = useState(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('We are currently working on something amazing. Check back soon!');
  
  // Section visibility states
  const [sectionVisibility, setSectionVisibility] = useState({
    beats: true,
    services: true,
    courses: true,
    studio: true
  });
  
  // Video form
  const [videoForm, setVideoForm] = useState({
    title: '', description: '', video_url: '', thumbnail_url: '',
    duration_minutes: 0, tier: 'entry', category: 'fundamentals', order: 0
  });
  
  // Beat form
  const [beatForm, setBeatForm] = useState({
    title: '', genre: 'Hip Hop', bpm: 140, key: 'C Minor', mood: 'Dark',
    audio_url: '', preview_url: '', image_url: '',
    lease_price: 29.99, premium_price: 79.99, exclusive_price: 299.99,
    duration: 180, tags: []
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchData();
    fetchMaintenanceStatus();
    fetchSectionVisibility();
  }, [user, navigate]);

  const fetchSectionVisibility = async () => {
    try {
      const response = await axios.get(`${API}/settings/sections`);
      setSectionVisibility(response.data);
    } catch (e) { console.error(e); }
  };

  const toggleSectionVisibility = async (section) => {
    try {
      const newValue = !sectionVisibility[section];
      await axios.post(`${API}/admin/sections?${section}=${newValue}`, {}, { withCredentials: true });
      setSectionVisibility(prev => ({ ...prev, [section]: newValue }));
      if (refreshSectionVisibility) refreshSectionVisibility();
      toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} section ${newValue ? 'shown' : 'hidden'}`);
    } catch (e) { toast.error('Failed to update section visibility'); }
  };

  const fetchMaintenanceStatus = async () => {
    try {
      const response = await axios.get(`${API}/settings/maintenance`);
      setMaintenanceMode(response.data.enabled);
      if (response.data.message) setMaintenanceMessage(response.data.message);
    } catch (e) { console.error(e); }
  };

  const toggleMaintenance = async () => {
    try {
      const newState = !maintenanceMode;
      await axios.post(`${API}/admin/maintenance?enabled=${newState}&message=${encodeURIComponent(maintenanceMessage)}`, {}, { withCredentials: true });
      setMaintenanceMode(newState);
      toast.success(newState ? 'Site is now in maintenance mode' : 'Site is now live!');
    } catch (e) { toast.error('Failed to toggle maintenance mode'); }
  };

  const fetchData = async () => {
    try {
      const [videosRes, studentsRes, proofsRes, bookingsRes, beatsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/admin/course/videos`, { withCredentials: true }),
        axios.get(`${API}/admin/course/students`, { withCredentials: true }),
        axios.get(`${API}/admin/etransfer/proofs`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/studio/bookings`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/beats`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/services`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setVideos(videosRes.data);
      setStudents(studentsRes.data);
      setProofs(proofsRes.data);
      setBookings(bookingsRes.data);
      setBeats(beatsRes.data);
      setServices(servicesRes.data);
    } catch (e) {
      console.error(e);
      if (e.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  // Beat CRUD
  const handleBeatSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/beats/${editingItem.beat_id}`, beatForm, { withCredentials: true });
        toast.success('Beat updated');
      } else {
        await axios.post(`${API}/admin/beats`, beatForm, { withCredentials: true });
        toast.success('Beat added');
      }
      setShowAddModal(false);
      setEditingItem(null);
      resetBeatForm();
      fetchData();
    } catch (e) { toast.error('Failed to save beat'); }
  };

  const handleDeleteBeat = async (beatId) => {
    if (!window.confirm('Delete this beat?')) return;
    try {
      await axios.delete(`${API}/admin/beats/${beatId}`, { withCredentials: true });
      toast.success('Beat deleted');
      fetchData();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const editBeat = (beat) => {
    setEditingItem(beat);
    setBeatForm({
      title: beat.title, genre: beat.genre, bpm: beat.bpm, key: beat.key, mood: beat.mood,
      audio_url: beat.audio_url, preview_url: beat.preview_url || '', image_url: beat.image_url || '',
      lease_price: beat.lease_price, premium_price: beat.premium_price, exclusive_price: beat.exclusive_price,
      duration: beat.duration || 180, tags: beat.tags || []
    });
    setModalType('beat');
    setShowAddModal(true);
  };

  const resetBeatForm = () => {
    setBeatForm({
      title: '', genre: 'Hip Hop', bpm: 140, key: 'C Minor', mood: 'Dark',
      audio_url: '', preview_url: '', image_url: '',
      lease_price: 29.99, premium_price: 79.99, exclusive_price: 299.99,
      duration: 180, tags: []
    });
  };

  // Video CRUD
  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/course/videos/${editingItem.video_id}`, videoForm, { withCredentials: true });
        toast.success('Video updated');
      } else {
        await axios.post(`${API}/admin/course/videos`, videoForm, { withCredentials: true });
        toast.success('Video added');
      }
      setShowAddModal(false);
      setEditingItem(null);
      resetVideoForm();
      fetchData();
    } catch (e) { toast.error('Failed to save video'); }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await axios.delete(`${API}/admin/course/videos/${videoId}`, { withCredentials: true });
      toast.success('Video deleted');
      fetchData();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const editVideo = (video) => {
    setEditingItem(video);
    setVideoForm({
      title: video.title, description: video.description, video_url: video.video_url,
      thumbnail_url: video.thumbnail_url || '', duration_minutes: video.duration_minutes || 0,
      tier: video.tier, category: video.category, order: video.order || 0
    });
    setModalType('video');
    setShowAddModal(true);
  };

  const resetVideoForm = () => {
    setVideoForm({ title: '', description: '', video_url: '', thumbnail_url: '', duration_minutes: 0, tier: 'entry', category: 'fundamentals', order: 0 });
  };

  // Service price update
  const handleServicePriceUpdate = async (serviceId, newPrice) => {
    try {
      await axios.put(`${API}/admin/services/${serviceId}?price=${newPrice}`, {}, { withCredentials: true });
      toast.success('Price updated');
      fetchData();
    } catch (e) { toast.error('Failed to update price'); }
  };

  // Booking management
  const handleConfirmBooking = async (bookingId) => {
    try {
      await axios.post(`${API}/studio/bookings/${bookingId}/confirm`, {}, { withCredentials: true });
      toast.success('Booking confirmed!');
      fetchData();
    } catch (e) { toast.error('Failed to confirm booking'); }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Cancel this booking?')) return;
    try {
      await axios.post(`${API}/studio/bookings/${bookingId}/cancel`, {}, { withCredentials: true });
      toast.success('Booking cancelled');
      fetchData();
    } catch (e) { toast.error('Failed to cancel booking'); }
  };

  // E-transfer proof management
  const handleApproveProof = async (proofId) => {
    try {
      await axios.post(`${API}/admin/etransfer/approve/${proofId}`, {}, { withCredentials: true });
      toast.success('Payment approved!');
      fetchData();
    } catch (e) { toast.error('Failed to approve'); }
  };

  const handleRejectProof = async (proofId) => {
    if (!window.confirm('Reject this payment proof?')) return;
    try {
      await axios.post(`${API}/admin/etransfer/reject/${proofId}`, {}, { withCredentials: true });
      toast.success('Payment rejected');
      fetchData();
    } catch (e) { toast.error('Failed to reject'); }
  };

  const pendingProofs = proofs.filter(p => p.status === 'pending_review');
  const pendingBookings = bookings.filter(b => b.status === 'pending' || b.status === 'deposit_submitted' || b.status === 'awaiting_deposit');
  const tierLabels = { entry: 'Entry', standard: 'Standard', pro: 'Pro' };
  const tierColors = { entry: 'bg-blue-500/20 text-blue-400 border-blue-500/30', standard: 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30', pro: 'bg-[#FFD700]/20 text-[#FFD700] border-[#FFD700]/30' };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-32 px-6 lg:px-12" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Syne' }}>Admin <span className="gradient-text">Dashboard</span></h1>
            <p className="text-neutral-500">Manage beats, services, courses, and bookings</p>
          </div>
        </div>

        {/* Maintenance Mode Toggle */}
        <div className={`glass-card p-4 mb-4 ${maintenanceMode ? 'border-yellow-500/50' : 'border-green-500/30'}`}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${maintenanceMode ? 'bg-yellow-500/20' : 'bg-green-500/20'}`}>
                <Power size={24} className={maintenanceMode ? 'text-yellow-500' : 'text-green-500'} />
              </div>
              <div>
                <h3 className="font-bold text-white">Site Status</h3>
                <p className={`text-sm ${maintenanceMode ? 'text-yellow-500' : 'text-green-500'}`}>
                  {maintenanceMode ? '🔧 Maintenance Mode - Site is offline' : '✅ Live - Site is visible to everyone'}
                </p>
              </div>
            </div>
            <button onClick={toggleMaintenance} className={`px-6 py-3 rounded-xl font-bold transition-all ${maintenanceMode ? 'bg-green-500 hover:bg-green-600 text-black' : 'bg-yellow-500 hover:bg-yellow-600 text-black'}`}>
              {maintenanceMode ? 'Go Live' : 'Enable Maintenance'}
            </button>
          </div>
        </div>

        {/* Section Visibility Controls */}
        <div className="glass-card p-4 mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#D4AF37]/20">
              <Settings size={24} className="text-[#D4AF37]" />
            </div>
            <div>
              <h3 className="font-bold text-white">Section Visibility</h3>
              <p className="text-sm text-neutral-500">Toggle which sections appear on the website</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: 'beats', label: 'Beats', icon: Music },
              { key: 'services', label: 'Services', icon: DollarSign },
              { key: 'courses', label: 'Courses', icon: Video },
              { key: 'studio', label: 'Book Studio', icon: Calendar }
            ].map(section => (
              <button
                key={section.key}
                onClick={() => toggleSectionVisibility(section.key)}
                data-testid={`toggle-${section.key}-visibility`}
                className={`p-4 rounded-xl border transition-all flex flex-col items-center gap-2 ${
                  sectionVisibility[section.key] 
                    ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]' 
                    : 'bg-neutral-800/50 border-neutral-700 text-neutral-500'
                }`}
              >
                <section.icon size={24} />
                <span className="text-sm font-medium">{section.label}</span>
                <div className="flex items-center gap-1 text-xs">
                  {sectionVisibility[section.key] ? (
                    <><Eye size={14} /> Visible</>
                  ) : (
                    <><EyeOff size={14} /> Hidden</>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-[#D4AF37]/10 overflow-x-auto pb-2">
          {[
            { id: 'beats', icon: Music, label: 'Beats', count: beats.length },
            { id: 'services', icon: DollarSign, label: 'Services', count: services.length },
            { id: 'videos', icon: Video, label: 'Videos', count: videos.length },
            { id: 'bookings', icon: Calendar, label: 'Bookings', count: pendingBookings.length, alert: true },
            { id: 'payments', icon: Image, label: 'Payments', count: pendingProofs.length, alert: true },
            { id: 'students', icon: Users, label: 'Students', count: students.length },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap relative ${activeTab === tab.id ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'text-neutral-500 hover:text-[#D4AF37]'}`}>
              <tab.icon size={18} />
              {tab.label}
              {tab.alert && tab.count > 0 && (
                <span className="w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center text-white">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Beats Tab */}
        {activeTab === 'beats' && (
          <>
            <button onClick={() => { setEditingItem(null); resetBeatForm(); setModalType('beat'); setShowAddModal(true); }}
              className="btn-primary mb-6 flex items-center gap-2">
              <Plus size={18} /> Add Beat
            </button>
            {beats.length === 0 ? (
              <div className="text-center py-20 glass-card">
                <Music size={48} className="mx-auto mb-4 text-[#D4AF37]/30" />
                <p className="text-neutral-500">No beats yet. Add your first beat!</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {beats.map(beat => (
                  <div key={beat.beat_id} className="glass-card p-4">
                    <div className="aspect-square rounded-lg bg-neutral-800 mb-3 overflow-hidden">
                      {beat.image_url ? <img src={beat.image_url} alt={beat.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Music size={48} className="text-[#D4AF37]/30" /></div>}
                    </div>
                    <h3 className="font-bold text-white truncate">{beat.title}</h3>
                    <p className="text-sm text-neutral-500">{beat.bpm} BPM • {beat.key} • {beat.genre}</p>
                    <div className="flex gap-2 mt-2 text-xs">
                      <span className="px-2 py-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded">${beat.lease_price}</span>
                      <span className="px-2 py-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded">${beat.premium_price}</span>
                      <span className="px-2 py-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded">${beat.exclusive_price}</span>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => editBeat(beat)} className="flex-1 py-2 bg-neutral-800 rounded-lg text-sm text-neutral-300 hover:bg-neutral-700"><Edit size={14} className="inline mr-1" /> Edit</button>
                      <button onClick={() => handleDeleteBeat(beat.beat_id)} className="py-2 px-3 bg-red-500/20 rounded-lg text-red-500 hover:bg-red-500/30"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="space-y-4">
            <p className="text-neutral-400 mb-4">Click on a price to edit it</p>
            {services.map(service => (
              <div key={service.service_id} className="glass-card p-4 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-white">{service.name}</h3>
                  <p className="text-sm text-neutral-500">{service.description}</p>
                  <span className={`text-xs px-2 py-0.5 rounded ${service.category === 'course' ? 'bg-blue-500/20 text-blue-400' : 'bg-[#D4AF37]/20 text-[#D4AF37]'}`}>
                    {service.category || 'mixing'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-neutral-500">$</span>
                  <input
                    type="number"
                    defaultValue={service.price}
                    className="w-24 bg-neutral-800 border border-[#D4AF37]/20 rounded-lg px-3 py-2 text-[#D4AF37] font-bold text-right"
                    onBlur={(e) => {
                      const newPrice = parseFloat(e.target.value);
                      if (newPrice !== service.price && newPrice > 0) {
                        handleServicePriceUpdate(service.service_id, newPrice);
                      }
                    }}
                  />
                  <span className="text-neutral-500">CAD</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Videos Tab */}
        {activeTab === 'videos' && (
          <>
            <button onClick={() => { setEditingItem(null); resetVideoForm(); setModalType('video'); setShowAddModal(true); }}
              className="btn-primary mb-6 flex items-center gap-2">
              <Plus size={18} /> Add Video
            </button>
            {videos.length === 0 ? (
              <div className="text-center py-20 glass-card">
                <Video size={48} className="mx-auto mb-4 text-[#D4AF37]/30" />
                <p className="text-neutral-500">No videos yet. Add your first course video!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {videos.map(video => (
                  <div key={video.video_id} className="glass-card p-4 flex items-center gap-4">
                    <div className="w-32 h-20 rounded-lg bg-neutral-800 flex-shrink-0 overflow-hidden">
                      {video.thumbnail_url ? <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Video size={24} className="text-[#D4AF37]/30" /></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold truncate text-white">{video.title}</h3>
                      <p className="text-sm text-neutral-500 truncate">{video.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded text-xs border ${tierColors[video.tier]}`}>{tierLabels[video.tier]}</span>
                        <span className="text-xs text-neutral-500 capitalize">{video.category}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => editVideo(video)} className="p-2 text-neutral-400 hover:text-[#D4AF37]"><Edit size={18} /></button>
                      <button onClick={() => handleDeleteVideo(video.video_id)} className="p-2 text-neutral-400 hover:text-red-500"><Trash2 size={18} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="space-y-4">
            {bookings.length === 0 ? (
              <div className="text-center py-20 glass-card">
                <Calendar size={48} className="mx-auto mb-4 text-[#D4AF37]/30" />
                <p className="text-neutral-500">No studio bookings yet</p>
              </div>
            ) : (
              bookings.map(booking => (
                <div key={booking.booking_id} className="glass-card p-4">
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    {booking.deposit_proof_url && (
                      <div className="w-32 h-24 rounded-lg bg-neutral-800 flex-shrink-0 overflow-hidden">
                        <img src={booking.deposit_proof_url} alt="Deposit" className="w-full h-full object-cover cursor-pointer" onClick={() => window.open(booking.deposit_proof_url, '_blank')} />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <span className="font-bold text-white">{booking.client_name}</span>
                        <span className="text-[#D4AF37] text-sm">{booking.room_name}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${booking.status === 'confirmed' ? 'bg-green-500/20 text-green-500' : booking.status === 'deposit_submitted' ? 'bg-blue-500/20 text-blue-500' : booking.status === 'cancelled' ? 'bg-red-500/20 text-red-500' : 'bg-yellow-500/20 text-yellow-500'}`}>
                          {booking.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-neutral-400">
                        <span><Calendar size={14} className="inline mr-1 text-[#D4AF37]" />{new Date(booking.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                        <span><Clock size={14} className="inline mr-1 text-[#D4AF37]" />{booking.time} ({booking.duration_hours}h)</span>
                        <span className="gradient-text font-bold">${booking.price} CAD</span>
                      </div>
                      <div className="mt-2 text-sm text-neutral-500">{booking.client_email} • {booking.client_phone}</div>
                    </div>
                    {(booking.status === 'deposit_submitted' || booking.status === 'awaiting_deposit' || booking.status === 'pending') && (
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleConfirmBooking(booking.booking_id)} className="p-2 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30"><Check size={18} /></button>
                        <button onClick={() => handleCancelBooking(booking.booking_id)} className="p-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30"><X size={18} /></button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-4">
            {proofs.length === 0 ? (
              <div className="text-center py-20 glass-card">
                <Image size={48} className="mx-auto mb-4 text-[#D4AF37]/30" />
                <p className="text-neutral-500">No e-transfer proofs submitted yet</p>
              </div>
            ) : (
              proofs.map(proof => (
                <div key={proof.proof_id} className="glass-card p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-48 h-32 rounded-lg bg-neutral-800 flex-shrink-0 overflow-hidden">
                      {proof.proof_image_url ? <img src={proof.proof_image_url} alt="Payment proof" className="w-full h-full object-cover cursor-pointer" onClick={() => window.open(proof.proof_image_url, '_blank')} /> : <div className="w-full h-full flex items-center justify-center"><Image size={24} className="text-[#D4AF37]/30" /></div>}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-mono text-sm text-neutral-500">{proof.order_id}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${proof.status === 'pending_review' ? 'bg-yellow-500/20 text-yellow-500' : proof.status === 'approved' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                          {proof.status.replace('_', ' ')}
                        </span>
                      </div>
                      {proof.sender_name && <p className="text-sm text-neutral-400">From: {proof.sender_name}</p>}
                      <p className="text-xs text-neutral-600 mt-2">Submitted: {new Date(proof.submitted_at).toLocaleString()}</p>
                    </div>
                    {proof.status === 'pending_review' && (
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleApproveProof(proof.proof_id)} className="p-2 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30"><Check size={18} /></button>
                        <button onClick={() => handleRejectProof(proof.proof_id)} className="p-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30"><X size={18} /></button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <div className="space-y-4">
            {students.length === 0 ? (
              <div className="text-center py-20 glass-card">
                <Users size={48} className="mx-auto mb-4 text-[#D4AF37]/30" />
                <p className="text-neutral-500">No students enrolled yet</p>
              </div>
            ) : (
              students.map((student, i) => (
                <div key={i} className="glass-card p-4 flex items-center gap-4">
                  {student.picture ? <img src={student.picture} alt="" className="w-12 h-12 rounded-full" /> : <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center"><User size={20} className="text-[#D4AF37]" /></div>}
                  <div className="flex-1">
                    <p className="font-medium text-white">{student.name}</p>
                    <p className="text-sm text-neutral-500">{student.email}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm border ${tierColors[student.tier]}`}>{tierLabels[student.tier]}</span>
                </div>
              ))
            )}
          </div>
        )}

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 overflow-y-auto">
            <div className="bg-[#0a0a0a] border border-[#D4AF37]/20 rounded-2xl p-6 w-full max-w-lg my-8" style={{ maxHeight: 'calc(100vh - 64px)', overflowY: 'auto' }}>
              <h2 className="text-xl font-bold mb-6 text-white" style={{ fontFamily: 'Syne' }}>
                {editingItem ? 'Edit' : 'Add'} {modalType === 'beat' ? 'Beat' : 'Video'}
              </h2>
              
              {modalType === 'beat' ? (
                <form onSubmit={handleBeatSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm text-neutral-400 mb-1">Title *</label>
                    <input type="text" value={beatForm.title} onChange={(e) => setBeatForm({ ...beatForm, title: e.target.value })} className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white" required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-neutral-400 mb-1">Genre</label>
                      <select value={beatForm.genre} onChange={(e) => setBeatForm({ ...beatForm, genre: e.target.value })} className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white">
                        {['Hip Hop', 'Trap', 'R&B', 'Pop', 'Drill', 'Lo-Fi', 'Afrobeat'].map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-neutral-400 mb-1">BPM</label>
                      <input type="number" value={beatForm.bpm} onChange={(e) => setBeatForm({ ...beatForm, bpm: parseInt(e.target.value) })} className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-neutral-400 mb-1">Key</label>
                      <input type="text" value={beatForm.key} onChange={(e) => setBeatForm({ ...beatForm, key: e.target.value })} className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white" placeholder="C Minor" />
                    </div>
                    <div>
                      <label className="block text-sm text-neutral-400 mb-1">Mood</label>
                      <select value={beatForm.mood} onChange={(e) => setBeatForm({ ...beatForm, mood: e.target.value })} className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white">
                        {['Dark', 'Chill', 'Energetic', 'Sad', 'Happy', 'Aggressive', 'Romantic'].map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                  </div>
                  
                  {/* Cover Image - Drag & Drop */}
                  <DragDropUpload
                    type="image"
                    value={beatForm.image_url}
                    onChange={(url) => setBeatForm({ ...beatForm, image_url: url })}
                    label="Cover Image"
                  />
                  
                  {/* Full Audio - Drag & Drop */}
                  <DragDropUpload
                    type="audio"
                    value={beatForm.audio_url}
                    onChange={(url) => setBeatForm({ ...beatForm, audio_url: url })}
                    label="Full Audio (MP3) *"
                  />
                  
                  {/* Preview Audio - Drag & Drop */}
                  <DragDropUpload
                    type="audio"
                    value={beatForm.preview_url}
                    onChange={(url) => setBeatForm({ ...beatForm, preview_url: url })}
                    label="Preview Audio (1 min clip - optional)"
                  />
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm text-neutral-400 mb-1">Lease $</label>
                      <input type="number" step="0.01" value={beatForm.lease_price} onChange={(e) => setBeatForm({ ...beatForm, lease_price: parseFloat(e.target.value) })} className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white" />
                    </div>
                    <div>
                      <label className="block text-sm text-neutral-400 mb-1">Premium $</label>
                      <input type="number" step="0.01" value={beatForm.premium_price} onChange={(e) => setBeatForm({ ...beatForm, premium_price: parseFloat(e.target.value) })} className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white" />
                    </div>
                    <div>
                      <label className="block text-sm text-neutral-400 mb-1">Exclusive $</label>
                      <input type="number" step="0.01" value={beatForm.exclusive_price} onChange={(e) => setBeatForm({ ...beatForm, exclusive_price: parseFloat(e.target.value) })} className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white" />
                    </div>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button type="button" onClick={() => setShowAddModal(false)} className="btn-secondary flex-1">Cancel</button>
                    <button type="submit" className="btn-primary flex-1">{editingItem ? 'Save Changes' : 'Add Beat'}</button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleVideoSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm text-neutral-400 mb-1">Title *</label>
                    <input type="text" value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white" required />
                  </div>
                  <div>
                    <label className="block text-sm text-neutral-400 mb-1">Description *</label>
                    <textarea value={videoForm.description} onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })} className="w-full h-24 bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white" required />
                  </div>
                  <div>
                    <label className="block text-sm text-neutral-400 mb-1">Video URL *</label>
                    <input type="url" value={videoForm.video_url} onChange={(e) => setVideoForm({ ...videoForm, video_url: e.target.value })} className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white" placeholder="https://youtube.com/..." required />
                  </div>
                  <div>
                    <label className="block text-sm text-neutral-400 mb-1">Thumbnail URL</label>
                    <input type="url" value={videoForm.thumbnail_url} onChange={(e) => setVideoForm({ ...videoForm, thumbnail_url: e.target.value })} className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-neutral-400 mb-1">Tier Access</label>
                      <select value={videoForm.tier} onChange={(e) => setVideoForm({ ...videoForm, tier: e.target.value })} className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white">
                        <option value="entry">Entry (all tiers)</option>
                        <option value="standard">Standard (Standard + Pro)</option>
                        <option value="pro">Pro (Pro only)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-neutral-400 mb-1">Category</label>
                      <input type="text" value={videoForm.category} onChange={(e) => setVideoForm({ ...videoForm, category: e.target.value })} className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white" placeholder="fundamentals" />
                    </div>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button type="button" onClick={() => setShowAddModal(false)} className="btn-secondary flex-1">Cancel</button>
                    <button type="submit" className="btn-primary flex-1">{editingItem ? 'Save Changes' : 'Add Video'}</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
